import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/layout/Layout";
import Seo from "@/components/seo/Seo";
import LiveScoresTicker from "@/components/feature/LiveScoresTicker";
import PredictionCard from "@/components/feature/PredictionCard";
import DashboardAnalytics from "@/components/feature/DashboardAnalytics";
import SlipScoreboard from "@/components/feature/SlipScoreboard";
import usePickTransitions from "@/hooks/usePickTransitions";
import { predictionsService, liveService } from "@/api/services";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Crown, Sparkle } from "@phosphor-icons/react";
import EmptyPicks from "@/components/feature/EmptyPicks";

const STADIUM_BG =
    "https://images.unsplash.com/photo-1706675780107-7c43cc487928?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwzfHxmb290YmFsbCUyMHN0YWRpdW0lMjBuaWdodHxlbnwwfHx8fDE3Nzg5OTk4Mzl8MA&ixlib=rb-4.1.0&q=85";

export default function Dashboard() {
    const { user } = useAuth();
    const [tiers, setTiers] = useState({ 2: [], 5: [], 10: [] });
    const [generating, setGenerating] = useState(false);
    const [liveMatches, setLiveMatches] = useState([]);
    const [liveByFixture, setLiveByFixture] = useState({});
    const [loading, setLoading] = useState(true);
    const [openFixture, setOpenFixture] = useState(null);
    const [fixtureDetails, setFixtureDetails] = useState(null);
    const [customInput, setCustomInput] = useState("");
    const [customResults, setCustomResults] = useState([]);
    const [customLoading, setCustomLoading] = useState(false);

    const pickAlerts = usePickTransitions(tiers, liveByFixture, user?.userLevel);

    useEffect(() => {
        const collectFixtureIds = (t) =>
            Array.from(
                new Set(
                    Object.values(t || {})
                        .flat()
                        .map((p) => p?.fixtureId)
                        .filter(Boolean)
                )
            );

        const refreshLive = async (fixtureIds) => {
            try {
                const [live, byFix] = await Promise.all([
                    liveService.scores(),
                    fixtureIds.length
                        ? liveService.byFixtures(fixtureIds)
                        : Promise.resolve({}),
                ]);
                setLiveMatches(Array.isArray(live) ? live : []);
                setLiveByFixture(byFix && typeof byFix === "object" ? byFix : {});
            } catch (err) {
                // eslint-disable-next-line no-console
                console.warn("Live refresh failed:", err?.message || err);
            }
        };

        const load = async () => {
            try {
                const pred = await predictionsService.today();
                const nextTiers = pred?.tiers || { 2: [], 5: [], 10: [] };
                setTiers(nextTiers);
                setGenerating(!!pred?.generating);
                await refreshLive(collectFixtureIds(nextTiers));
            } catch (err) {
                // eslint-disable-next-line no-console
                console.warn("Dashboard load failed:", err?.message || err);
                toast.error("Could not load today's data");
            } finally {
                setLoading(false);
            }
        };
        load();
        const interval = setInterval(() => {
            // Re-read the latest tiers via the functional setter so the
            // closure doesn't capture a stale snapshot.
            setTiers((current) => {
                refreshLive(collectFixtureIds(current));
                return current;
            });
        }, 30000);
        return () => clearInterval(interval);
    }, []);

    const openMatch = async (id) => {
        setOpenFixture(id);
        setFixtureDetails(null);
        try {
            const det = await liveService.fixture(id);
            setFixtureDetails(det);
        } catch (err) {
            // eslint-disable-next-line no-console
            console.warn("Fixture load failed:", err?.message || err);
            toast.error("Could not load match details");
        }
    };

    const onCustomSubmit = async (e) => {
        e.preventDefault();
        if (user.userLevel !== "vip") {
            toast.error("VIP only feature");
            return;
        }
        const fixtures = customInput
            .split("\n")
            .map((s) => s.trim())
            .filter(Boolean)
            .slice(0, 3);
        if (fixtures.length === 0) {
            toast.error("Enter at least one fixture");
            return;
        }
        setCustomLoading(true);
        try {
            const data = await predictionsService.custom(fixtures);
            setCustomResults(data || []);
            toast.success(`Generated ${data?.length || 0} predictions`);
        } catch (err) {
            toast.error(err?.message || "AI prediction failed");
        } finally {
            setCustomLoading(false);
        }
    };

    return (
        <Layout>
            <Seo title="Dashboard" path="/dashboard" noIndex />

            <div className="max-w-7xl mx-auto px-4 md:px-8 pt-8">
                <div className="font-mono text-xs uppercase tracking-[0.4em] text-primary mb-2">
                    ▌ {new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}
                </div>
                <h1 className="text-3xl md:text-5xl font-black uppercase tracking-tighter">
                    Welcome back, <span className="text-primary">{user?.name || "Bettor"}</span>
                </h1>
            </div>

            <div className="mt-6">
                <LiveScoresTicker />
            </div>

            <SlipScoreboard
                tiers={tiers}
                liveByFixture={liveByFixture}
                userLevel={user?.userLevel}
                alerts={pickAlerts}
            />

            <div className="mt-10">
                <DashboardAnalytics />
            </div>

            {/* PREDICTIONS BY TIER */}
            <section className="max-w-7xl mx-auto px-4 md:px-8 py-12 space-y-12">
                {["2", "5", "10"].map((cat) => {
                    const list = tiers[cat] || [];
                    const locked =
                        (cat === "5" || cat === "10") && user?.userLevel !== "vip";
                    return (
                        <div key={cat}>
                            <div className="flex items-baseline justify-between mb-4 border-b border-border pb-3">
                                <h2 className="text-2xl md:text-3xl font-bold tracking-tight">
                                    <span className="text-primary font-mono">{cat}</span>-game slip
                                </h2>
                                <span className="font-mono text-xs uppercase tracking-widest text-zinc-500">
                                    {locked ? "LOCKED" : `${list.length} PICKS`}
                                </span>
                            </div>
                            {locked ? (
                                <LockedTier tier={cat} />
                            ) : loading ? (
                                <SkeletonGrid />
                            ) : list.length === 0 ? (
                                <EmptyPicks
                                    generating={generating}
                                    message={
                                        generating
                                            ? "Generating today's slip — refresh in 10–30 seconds."
                                            : "No fixtures in our covered leagues for this tier today."
                                    }
                                />
                            ) : (
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                    {list.map((p, i) => (
                                        <PredictionCard
                                            key={i}
                                            pick={p}
                                            live={
                                                p.fixtureId
                                                    ? liveByFixture[String(p.fixtureId)]
                                                    : undefined
                                            }
                                            testIdPrefix={`tier${cat}-${i}`}
                                        />
                                    ))}
                                </div>
                            )}
                        </div>
                    );
                })}
            </section>

            {/* QUICK AI PREDICTIONS */}
            <section className="max-w-7xl mx-auto px-4 md:px-8 py-12">
                <div className="bg-card border border-border p-6 md:p-10 relative overflow-hidden">
                    <div className="absolute inset-0 opacity-10" style={{ backgroundImage: `url(${STADIUM_BG})`, backgroundSize: "cover", backgroundPosition: "center" }} />
                    <div className="relative">
                        <div className="flex items-center gap-3 mb-2">
                            <Sparkle size={24} weight="duotone" className="text-primary" />
                            <h2 className="text-2xl md:text-3xl font-bold tracking-tight">Quick AI Predictions</h2>
                            <span className="font-mono text-[10px] uppercase tracking-widest px-2 py-1 bg-primary/10 text-primary border border-primary">
                                VIP
                            </span>
                        </div>
                        <p className="text-sm text-zinc-400 mb-6 max-w-2xl">
                            Paste up to 3 fixtures (one per line, format: <span className="font-mono text-primary">Home vs Away</span>). Our AI returns structured picks with markets, odds and confidence.
                        </p>
                        <form onSubmit={onCustomSubmit} className="space-y-4" data-testid="custom-predict-form">
                            <Textarea
                                rows={3}
                                value={customInput}
                                onChange={(e) => setCustomInput(e.target.value)}
                                placeholder="Arsenal vs Chelsea&#10;Real Madrid vs Barcelona"
                                className="rounded-none bg-background border-border focus:border-primary focus:ring-1 focus:ring-primary font-mono text-sm"
                                data-testid="custom-predict-input"
                            />
                            <Button
                                type="submit"
                                disabled={customLoading || user?.userLevel !== "vip"}
                                className="rounded-none bg-primary text-primary-foreground hover:bg-[#33FFB0] font-bold uppercase tracking-wider"
                                data-testid="custom-predict-submit"
                            >
                                {customLoading ? "ANALYSING..." : "Generate Picks"}
                            </Button>
                            {user?.userLevel !== "vip" && (
                                <p className="font-mono text-xs uppercase tracking-widest text-amber-400">
                                    🔐 Upgrade to VIP to use this feature.{" "}
                                    <Link to="/subscription" className="underline text-primary">
                                        Go to subscription →
                                    </Link>
                                </p>
                            )}
                        </form>
                        {customResults.length > 0 && (
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8">
                                {customResults.map((p, i) => (
                                    <PredictionCard
                                        key={i}
                                        pick={p}
                                        testIdPrefix={`custom-${i}`}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </section>

            {/* LIVE SCORES GRID */}
            <section className="max-w-7xl mx-auto px-4 md:px-8 py-12">
                <h2 className="text-2xl md:text-3xl font-bold tracking-tight mb-6 border-b border-border pb-3">
                    Live matches
                </h2>
                {liveMatches.length === 0 ? (
                    <div className="border border-dashed border-border p-8 text-center font-mono text-xs uppercase tracking-widest text-zinc-500">
                        No live matches right now
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {liveMatches.map((m) => (
                            <button
                                key={m.id}
                                type="button"
                                onClick={() => openMatch(m.id)}
                                className="text-left bg-card border border-border p-5 hover:border-primary/60 transition-snappy hover:-translate-y-0.5"
                                data-testid={`live-match-${m.id}`}
                            >
                                <div className="flex items-center gap-2 mb-2">
                                    <span className="inline-block w-1.5 h-1.5 rounded-full bg-red-500 live-dot" />
                                    <span className="font-mono text-[10px] uppercase tracking-widest text-red-400">
                                        LIVE · {m.status?.elapsed ? `${m.status.elapsed}'` : m.status?.long}
                                    </span>
                                </div>
                                <div className="font-bold text-white">{m.home}</div>
                                <div className="font-mono text-2xl text-primary my-1">
                                    {m.goals?.home ?? 0} : {m.goals?.away ?? 0}
                                </div>
                                <div className="font-bold text-white">{m.away}</div>
                                <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mt-2 truncate">
                                    {m.league} · {m.country}
                                </div>
                            </button>
                        ))}
                    </div>
                )}
            </section>

            <Dialog open={!!openFixture} onOpenChange={(o) => !o && setOpenFixture(null)}>
                <DialogContent className="bg-card border-border rounded-none max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="font-bold tracking-tight">
                            Match Details
                        </DialogTitle>
                    </DialogHeader>
                    {!fixtureDetails ? (
                        <p className="font-mono text-xs uppercase tracking-widest pulse-sharp text-zinc-500">
                            LOADING...
                        </p>
                    ) : (
                        <div className="space-y-3 text-sm">
                            <p className="font-bold text-lg">
                                {fixtureDetails.home} vs {fixtureDetails.away}
                            </p>
                            <p className="font-mono text-xs uppercase tracking-widest text-zinc-500">
                                {fixtureDetails.league} · {fixtureDetails.country}
                            </p>
                            <Row label="Score">
                                {fixtureDetails.goalsHome ?? 0}:{fixtureDetails.goalsAway ?? 0}
                            </Row>
                            <Row label="Status">
                                {fixtureDetails.status?.short || fixtureDetails.status?.long}
                            </Row>
                            <Row label="Venue">{fixtureDetails.venue || "—"}</Row>
                            <Row label="Referee">{fixtureDetails.referee || "—"}</Row>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </Layout>
    );
}

function Row({ label, children }) {
    return (
        <div className="flex justify-between border-t border-border pt-2">
            <span className="font-mono text-xs uppercase tracking-widest text-zinc-500">
                {label}
            </span>
            <span className="text-white">{children}</span>
        </div>
    );
}

function SkeletonGrid() {
    return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[0, 1, 2].map((i) => (
                <div key={i} className="bg-card border border-border p-5 h-44 pulse-sharp">
                    <div className="font-mono text-xs uppercase tracking-widest text-zinc-600">
                        CALCULATING ODDS...
                    </div>
                </div>
            ))}
        </div>
    );
}

function LockedTier({ tier }) {
    return (
        <div className="bg-card border border-dashed border-primary/30 p-8 text-center relative overflow-hidden">
            <Crown size={32} weight="duotone" className="text-primary mx-auto mb-3" />
            <p className="font-bold text-white mb-2">
                {tier}-game slip is locked
            </p>
            <p className="text-sm text-zinc-400 mb-4">
                Upgrade to VIP to unlock these picks.
            </p>
            <Link to="/subscription">
                <Button
                    className="rounded-none bg-primary text-primary-foreground hover:bg-[#33FFB0] font-bold uppercase tracking-wider"
                    data-testid={`unlock-tier-${tier}-btn`}
                >
                    Upgrade
                </Button>
            </Link>
        </div>
    );
}
